import React, { useState } from 'react'
import { Eye, Check, Star, Music, Camera, Palette, Code, Zap } from 'lucide-react'

export interface Template {
  id: string
  name: string
  category: 'music' | 'artist' | 'portfolio' | 'business' | 'blog' | 'defi' | 'nft' | 'dao' | 'gaming'
  description: string
  preview: string
  features: string[]
  icon: React.ReactNode
  colors: string[]
  components: any[]
  styles: any
}

const templates: Template[] = [
  {
    id: 'music-creator-pro',
    name: 'Music Creator Pro',
    category: 'music',
    description: 'Professional template for Web3 musicians, producers, and DJs. Features music player, NFT drops, and blockchain merch.',
    preview: '/api/placeholder/800/600',
    features: ['Music Player', 'NFT Drops', 'Blockchain Merch', 'Web3 Social', 'DAO Integration', 'Token Rewards'],
    icon: <Music className="w-8 h-8" />,
    colors: ['#8B5CF6', '#F59E0B', '#EF4444'],
    components: [],
    styles: {
      primaryColor: '#8B5CF6',
      secondaryColor: '#F59E0B',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'visual-artist-studio',
    name: 'Digital Artist Studio',
    category: 'artist',
    description: 'Perfect for digital artists, NFT creators, and Web3 designers. Showcase your blockchain art and collectibles.',
    preview: '/api/placeholder/800/600',
    features: ['NFT Gallery', 'Artist Bio', 'Commission NFTs', 'Blockchain Links', 'DAO Voting', 'Royalty Tracking'],
    icon: <Camera className="w-8 h-8" />,
    colors: ['#EC4899', '#8B5CF6', '#06B6D4'],
    components: [],
    styles: {
      primaryColor: '#EC4899',
      secondaryColor: '#8B5CF6',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'creator-hub',
    name: 'Web3 Creator Hub',
    category: 'portfolio',
    description: 'All-in-one Web3 platform for content creators. Includes monetization, community, and blockchain features.',
    preview: '/api/placeholder/800/600',
    features: ['Content Gallery', 'Token Gating', 'Subscription NFTs', 'Tip in Crypto', 'DAO Community', 'Revenue Analytics'],
    icon: <Zap className="w-8 h-8" />,
    colors: ['#F59E0B', '#10B981', '#EF4444'],
    components: [],
    styles: {
      primaryColor: '#F59E0B',
      secondaryColor: '#10B981',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'nft-collection',
    name: 'NFT Collection',
    category: 'nft',
    description: 'Showcase your NFT collection with marketplace integration and blockchain verification.',
    preview: '/api/placeholder/800/600',
    features: ['NFT Display', 'Marketplace Links', 'Blockchain Verification', 'Collection Stats', 'Floor Price', 'Trading History'],
    icon: <Star className="w-8 h-8" />,
    colors: ['#374151', '#6B7280', '#F59E0B'],
    components: [],
    styles: {
      primaryColor: '#374151',
      secondaryColor: '#6B7280',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'web3-blog',
    name: 'Web3 Blog',
    category: 'blog',
    description: 'Modern blog template for Web3 developers, crypto enthusiasts, and blockchain content creators.',
    preview: '/api/placeholder/800/600',
    features: ['Blog Posts', 'Code Snippets', 'Crypto Integration', 'DAO Discussions', 'Newsletter', 'Token Comments'],
    icon: <Code className="w-8 h-8" />,
    colors: ['#1F2937', '#3B82F6', '#10B981'],
    components: [],
    styles: {
      primaryColor: '#1F2937',
      secondaryColor: '#3B82F6',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'dao-governance',
    name: 'DAO Governance',
    category: 'dao',
    description: 'Professional template for DAOs, governance protocols, and decentralized organizations.',
    preview: '/api/placeholder/800/600',
    features: ['Governance Dashboard', 'Proposal System', 'Voting Interface', 'Treasury Stats', 'Member Directory', 'Token Economics'],
    icon: <Zap className="w-8 h-8" />,
    colors: ['#7C3AED', '#F59E0B', '#EF4444'],
    components: [],
    styles: {
      primaryColor: '#7C3AED',
      secondaryColor: '#F59E0B',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'defi-protocol',
    name: 'DeFi Protocol',
    category: 'defi',
    description: 'Template for DeFi protocols, yield farming platforms, and decentralized finance projects.',
    preview: '/api/placeholder/800/600',
    features: ['Protocol Dashboard', 'APY Display', 'Liquidity Pools', 'Token Swaps', 'Governance', 'Security Audits'],
    icon: <Star className="w-8 h-8" />,
    colors: ['#00BCD4', '#4CAF50', '#8BC34A'],
    components: [],
    styles: {
      primaryColor: '#00BCD4',
      secondaryColor: '#4CAF50',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'gaming-metaverse',
    name: 'Gaming Metaverse',
    category: 'gaming',
    description: 'Template for Web3 games, metaverse projects, and blockchain gaming platforms.',
    preview: '/api/placeholder/800/600',
    features: ['Game Gallery', 'NFT Assets', 'Token Rewards', 'Leaderboards', 'Marketplace', 'Community Hub'],
    icon: <Zap className="w-8 h-8" />,
    colors: ['#E91E63', '#9C27B0', '#673AB7'],
    components: [],
    styles: {
      primaryColor: '#E91E63',
      secondaryColor: '#9C27B0',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'crypto-influencer',
    name: 'Crypto Influencer',
    category: 'portfolio',
    description: 'Perfect for crypto influencers, traders, and Web3 content creators.',
    preview: '/api/placeholder/800/600',
    features: ['Portfolio Tracker', 'Trade Analysis', 'Social Proof', 'Newsletter', 'Premium Content', 'Affiliate Links'],
    icon: <Star className="w-8 h-8" />,
    colors: ['#FF9800', '#FF5722', '#E91E63'],
    components: [],
    styles: {
      primaryColor: '#FF9800',
      secondaryColor: '#FF5722',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'web3-startup',
    name: 'Web3 Startup',
    category: 'business',
    description: 'Modern landing page for Web3 startups, crypto projects, and blockchain companies.',
    preview: '/api/placeholder/800/600',
    features: ['Project Overview', 'Token Economics', 'Roadmap', 'Team Profiles', 'Whitepaper', 'Community Links'],
    icon: <Code className="w-8 h-8" />,
    colors: ['#2196F3', '#21CBF3', '#03DAC6'],
    components: [],
    styles: {
      primaryColor: '#2196F3',
      secondaryColor: '#21CBF3',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'nft-marketplace',
    name: 'NFT Marketplace',
    category: 'nft',
    description: 'Template for NFT marketplaces, art platforms, and digital collectible stores.',
    preview: '/api/placeholder/800/600',
    features: ['NFT Gallery', 'Auction System', 'Creator Profiles', 'Royalty Settings', 'Wallet Integration', 'Trading Stats'],
    icon: <Star className="w-8 h-8" />,
    colors: ['#9C27B0', '#E91E63', '#F44336'],
    components: [],
    styles: {
      primaryColor: '#9C27B0',
      secondaryColor: '#E91E63',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  },
  {
    id: 'blockchain-developer',
    name: 'Blockchain Developer',
    category: 'portfolio',
    description: 'Portfolio template for blockchain developers, smart contract engineers, and Web3 builders.',
    preview: '/api/placeholder/800/600',
    features: ['Project Showcase', 'Code Repositories', 'Smart Contracts', 'DeFi Integrations', 'Audit Reports', 'Technical Blog'],
    icon: <Code className="w-8 h-8" />,
    colors: ['#1F2937', '#3B82F6', '#10B981'],
    components: [],
    styles: {
      primaryColor: '#1F2937',
      secondaryColor: '#3B82F6',
      fontFamily: 'Georgia, serif',
      backgroundColor: '#1F2937'
    }
  }
]

interface TemplateSelectorProps {
  onSelectTemplate: (template: Template) => void
  onPreviewTemplate?: (template: Template) => void
  onClose: () => void
}

export default function TemplateSelector({ onSelectTemplate, onPreviewTemplate, onClose }: TemplateSelectorProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)

  const categories = [
    { id: 'all', name: 'All Templates', icon: <Palette className="w-4 h-4" /> },
    { id: 'music', name: 'Music Creators', icon: <Music className="w-4 h-4" /> },
    { id: 'artist', name: 'Digital Artists', icon: <Camera className="w-4 h-4" /> },
    { id: 'portfolio', name: 'Creator Portfolios', icon: <Star className="w-4 h-4" /> },
    { id: 'blog', name: 'Web3 Blogs', icon: <Code className="w-4 h-4" /> },
    { id: 'business', name: 'Web3 Businesses', icon: <Zap className="w-4 h-4" /> },
    { id: 'defi', name: 'DeFi Protocols', icon: <Zap className="w-4 h-4" /> },
    { id: 'nft', name: 'NFT Projects', icon: <Star className="w-4 h-4" /> },
    { id: 'dao', name: 'DAO Governance', icon: <Zap className="w-4 h-4" /> },
    { id: 'gaming', name: 'Gaming/Metaverse', icon: <Star className="w-4 h-4" /> }
  ]

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(template => template.category === selectedCategory)


  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl border border-white/20 max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Choose Your Template
              </h2>
              <p className="text-purple-200">
                Select a beautiful template to start building your website
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="p-6 border-b border-white/20">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-gold-500 text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {category.icon}
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-10 text-purple-300">
              <p className="text-lg mb-2">No templates found</p>
              <p className="text-sm">Try selecting a different category</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className={`bg-white/5 backdrop-blur-sm border rounded-xl overflow-hidden transition-all duration-300 cursor-pointer group ${
                  selectedTemplate === template.id
                    ? 'border-gold-400 bg-gold-500/10'
                    : 'border-white/20 hover:border-white/40 hover:bg-white/10'
                }`}
                onClick={() => setSelectedTemplate(template.id)}
              >
                {/* Template Preview */}
                <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      {template.icon}
                    </div>
                    <p className="text-white text-sm">{template.name}</p>
                  </div>
                </div>

                {/* Template Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-semibold">{template.name}</h3>
                  </div>
                  
                  <p className="text-purple-200 text-sm mb-3 line-clamp-2">
                    {template.description}
                  </p>

                  {/* Features */}
                  <div className="mb-3">
                    <p className="text-white text-xs mb-2">Features:</p>
                    <div className="flex flex-wrap gap-1">
                      {template.features.slice(0, 3).map((feature, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded"
                        >
                          {feature}
                        </span>
                      ))}
                      {template.features.length > 3 && (
                        <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded">
                          +{template.features.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Color Palette */}
                  <div className="flex items-center gap-2">
                    <span className="text-white text-xs">Colors:</span>
                    <div className="flex gap-1">
                      {template.colors.map((color, index) => (
                        <div
                          key={index}
                          className="w-4 h-4 rounded-full border border-white/20"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="absolute top-2 right-2 flex gap-2">
                  {onPreviewTemplate && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onPreviewTemplate(template)
                      }}
                      className="w-8 h-8 bg-blue-500/80 hover:bg-blue-500 rounded-full flex items-center justify-center transition-colors"
                      title="Preview Template"
                    >
                      <Eye className="w-4 h-4 text-white" />
                    </button>
                  )}
                  {selectedTemplate === template.id && (
                    <div className="w-8 h-8 bg-gold-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-black" />
                    </div>
                  )}
                </div>
              </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/20 flex items-center justify-between">
          <div className="text-purple-200 text-sm">
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} available
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const template = templates.find(t => t.id === selectedTemplate)
                if (template) {
                  onSelectTemplate(template)
                }
              }}
              disabled={!selectedTemplate}
              className="px-6 py-2 bg-gold-500 text-black rounded-lg hover:bg-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Use Template
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
